import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type MetricRecord from 'webapp/models/metric';

export default class MetricSerializer extends ApplicationSerializer {
	override getReadOnlyFields(_json: Record<string, unknown>): RecordData.Metric.ReadOnlyKey[] {
		return ['cloneFrom'];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.Metric.Raw | RecordData.Metric.Serialized,
		prop: string | undefined,
	) {
		const { settings, ...data } = json;
		const attributes: Partial<RecordAttributes<MetricRecord>> & { id?: string | number } = {
			...data,
		};

		if (settings) {
			attributes.metricType = settings.metricType;
			attributes.aggregationFunction = settings.aggregationFunction || null;
			attributes.granulationOrigin = settings.granulationOrigin || null;
			attributes.dimension = settings.dimension || null;
			attributes.hasFutureData = !!settings.hasFutureData;
			attributes.isHistoryChanging = !!settings.isHistoryChanging;
			attributes.ensureUnique = !!settings.ensureUnique;
			attributes.isAppend = !!settings.isAppend;
			attributes.isHistoricalDataPersistenceEnabled =
				settings.isHistoricalDataPersistenceEnabled ?? null;
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.Metric.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<MetricRecord>,
			RecordData.Metric.ReadOnlyKey
		>;

		return {
			// isHistoricalDataPersistenceEnabled stays at the top level too (see RecordData.Metric.SettingsKey)
			...omit(
				attributes,
				'metricType',
				'aggregationFunction',
				'granulationOrigin',
				'dimension',
				'hasFutureData',
				'isHistoryChanging',
				'ensureUnique',
				'isAppend',
				'verificationInfo',
			),
			settings: {
				metricType: attributes.metricType,
				aggregationFunction: attributes.aggregationFunction,
				granulationOrigin: attributes.granulationOrigin,
				hasFutureData: attributes.hasFutureData,
				isHistoryChanging: attributes.isHistoryChanging,
				ensureUnique: attributes.ensureUnique,
				isAppend: attributes.isAppend,
				isHistoricalDataPersistenceEnabled: attributes.isHistoricalDataPersistenceEnabled,
			},
		};
	}
}
